/** @format */

import React from "react";
import { Form, Icon, Image } from "semantic-ui-react";
import { convertToBEDate } from "../../../utils/tsUtils";
import { saveAs } from "file-saver";
import DisplayContentImage from "./DisplayContentImage";
import DisplayContentFile, { getContentFileName } from "./DisplayContentFile";
import ButtonDownloadFile from "./ButtonDownloadFile";
import ChatVideoContent from "./ChatVideoContent";
import ChatLinkifyMessage, { hasMarkdownPattern } from "./ChatLinkifyMessage";
import moment from "moment";
import MessageContentHTML from "./MessageContentHTML";

export const isValidHttpUrl = (plaintext) => {
  let url;
  try {
    url = new URL(plaintext);
  } catch (_) {
    return false;
  }
  return url.protocol === "http:" || url.protocol === "https:";
};

const isMobile = () => {
  let isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  return isMobile;
};

export const downloadOrOpenFile = (data: {
  content?: string;
  content_file?: string;
  downloadFile?: boolean;
}) => {
  const dateNow = moment().format("DD_MM_YYYY");
  const fileName = getContentFileName(data.content);

  if (data.downloadFile || fileName) {
    saveAs(data.content_file, fileName || `file_${dateNow}`);
  } else {
    let newUrl = data.content_file;
    if (isMobile()) {
      newUrl = newUrl.includes("?")
        ? `${newUrl}&openExternalBrowser=1`
        : `${newUrl}?openExternalBrowser=1`;
    }
    window.open(`${newUrl}`);
  }
};

interface MyMessageProps {
  isBot: boolean;
  isMobile: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onOpenVideoCall: () => void;
  onNavigationMessage: () => void;
  onClickLink: () => void;
  downloadFile: boolean;
  language: string;
  showAuthorName?: boolean;
  showFileSize?: boolean;
  isMediaViewer?: boolean;
  onLoadImage?: (id: any) => void;
}

const DEFAULT_PROPS = {
  isBot: false,
  isMobile: false,
  isSelected: false,
  onSelect: () => {},
  onOpenVideoCall: ({ url }) => {},
  onNavigationMessage: ({ content = "" }) => content,
  onMobileOpenImage: () => {},
  onClickLink: () => {},
  downloadFile: false,
  language: "th-TH",
};
const MyMessage = (propsParam: MyMessageProps) => {
  const props = {
    ...DEFAULT_PROPS,
    ...Object.fromEntries(Object.entries(propsParam).filter(([_, value]) => value !== undefined)),
  };

  const dateFormat = "DD/MM/YYYY";
  const generateRead = () => {
    if (props.message.user_read_count > 1) {
      return props.showAuthorName ? "" : "Read";
      // return <>{`Read ${props.message.user_read_count}`}</>;
    } else if (props.message.user_read_count > 0) {
      return props.showAuthorName ? "" : "Read";
    } else if (props.message.hasOwnProperty("sendFail")) {
      if (props.message.sendFail) {
        return "Failed";
      }
      // else {
      //   return "Delivering";
      // }
    }
    // else {
    //   return "Delivered";
    // }
  };

  const createMessage = () => {
    if (props.message.content_type === "file") {
      return (
        <DisplayContentFile
          content={props.message.content}
          file={props.message.content_file}
          showFileSize={props.showFileSize}
        />
      );
    } else if (props.message.content_type === "image") {
      if (!props.message.content_file) {
        return "Unable to get Image";
      }
      if (typeof props.message.content_file === "object") {
        return <Image src={URL.createObjectURL(props.message.content_file)} />;
      }
      return (
        <DisplayContentImage
          id={props.message.id}
          isMediaViewer={props.isMediaViewer}
          isMobile={props.isMobile}
          src={props.message.content_file}
          onLoadImage={props.onLoadImage}
        />
      );
    } else if (props.message.content_type === "video") {
      if (!props.message.content_file) {
        return "Unable to get video";
      }

      return (
        <ChatVideoContent
          file={props.message.content_file}
          isMediaViewer={props.isMediaViewer}
          isMobile={props.isMobile}
        />
      );
    } else if (props.message.content_type === "vid_call") {
      return <Icon name="call square" size="big" color="green" />;
    } else if (props.message.content_type === "end_call") {
      return <Icon name="call square" size="big" color="grey" />;
    } else if (props.message.content_type === "Preverification") {
      return props.onNavigationMessage({
        content: props.message.content,
        contentPayload: props.message.content_payload,
        contentType: props.message.content_type,
        channelId: props.message.channel,
      });
    } else if (props.message.content_type === "navigation") {
      return props.onNavigationMessage({
        content: props.message.content,
        contentPayload: props.message.content_payload,
        contentType: props.message.content_type,
      });
    } else if (props.message.content_type === "html") {
      return <MessageContentHTML content={props.message.content} />;
    } else if (props.message.content_type === "post") {
      return props.onNavigationMessage({
        content: props.message.content,
        contentPayload: props.message.content_payload,
        contentType: props.message.content_type,
        channelId: props.message.channel,
        author: props.message.author,
        sendAt: props.message.send_at,
        messageId: props.message.id,
        onLoadImage: props.onLoadImage,
      });
    } else if (["patient_appointment"].includes(props.message.content_type)) {
      return props.onNavigationMessage({
        content: props.message.content,
        contentPayload: props.message.content_payload,
        contentType: props.message.content_type,
        channelId: props.message.channel,
        author: props.message.author,
      });
    } else {
      // Markdown Message for form
      // text_hyper_link is custom type for teleDesktop
      if (
        props.message.content.includes("http") &&
        (props.message.content.includes("/form/") ||
          props.message.content.includes("/assessment-form/")) &&
        (!props.isMobile || hasMarkdownPattern(props.message.content))
      ) {
        return (
          <ChatLinkifyMessage
            content={props.message.content}
            contentPayload={props.message.content_payload}
            isMobile={props.isMobile}
            onNavigationMessage={props.onNavigationMessage}
            onClickLink={props.onClickLink}
          />
        );
      } else if (props.message.content.includes("http")) {
        return (
          <ChatLinkifyMessage
            content={props.message.content}
            isMobile={props.isMobile}
            isPlainText
          />
        );
      } else {
        return props.message.content;
      }
    }
  };

  return (
    <Form className="MyMessage">
      {props.showAuthorName && props.message.author?.full_name && (
        <div
          style={{
            direction: "rtl",
            display: "flex",
            fontSize: "13.75px",
            marginRight: "4px",
          }}
        >
          {props.message.author?.full_name}
        </div>
      )}
      <Form.Group inline style={{ margin: 0 }}>
        <Form.Field
          className={
            "content" + (props.isSelected ? " selected " : "") + (props.isBot ? " chatbot " : "")
          }
          style={{
            direction: "initial",
            maxWidth: "500px",
            wordBreak: "break-word",
            ...(props.message.content_type === "patient_appointment" && {
              maxWidth: "75%",
              overflow: "hidden",
              margin: "0.25rem 0",
            }),
          }}
          {...(props.isMobile &&
            props.message.content_type === "file" && {
              href: props.message.content_file,
            })}
          onClick={() => {
            // props.onSelect(props.message.id)
            if (props.message.sendFail) {
              props.onResend({ message: props.message });
            }
            if (props.message.content_type == "vid_call" && !props.isMobile) {
              props.onOpenVideoCall({ url: props.message.content });
            }
            if (props.message.content_type === "file") {
              downloadOrOpenFile({
                downloadFile: props.downloadFile,
                ...props.message,
              });
            }
            if (props.isMobile && props.message.content_type === "image") {
              props.onMobileOpenImage(props.message.content_file);
            }
          }}
        >
          {createMessage()}
        </Form.Field>
        <Form.Field className={"sendDateTime" + (props.isBot ? " chatbot " : "")}>
          {generateRead()}
          {props.message.send_at ? (
            <>
              <div style={{ margin: 0 }}>
                {props.message.send_at.split(" ")[1]}
                {props.showAuthorName && generateRead() === "" && (
                  <label style={{ marginRight: "2px" }}>
                    <MessageReadIcon />
                  </label>
                )}
              </div>
              <div>
                {props.language === "th-TH"
                  ? convertToBEDate({
                      date: props.message.send_at.split(" ")[0],
                    })
                  : moment(props.message.send_at.split(" ")[0], dateFormat).format(dateFormat)}
              </div>
            </>
          ) : props.message.sendFail ? (
            <Icon name="redo" onClick={() => props.onResend({ message: props.message })} />
          ) : (
            <Icon name="reply" />
          )}
        </Form.Field>
      </Form.Group>
      <ButtonDownloadFile
        content={props.message.content}
        contentFile={props.message.content_file}
        contentType={props.message.content_type}
        showFileSize={props.showFileSize}
      />

      {/* <Form.Group
        style={{ margin: 0, display: props.isSelected ? null : "none" }}
      >
        {generateRead()}
      </Form.Group> */}
    </Form>
  );
};

const MessageReadIcon = () => (
  <svg fill="none" height="12" viewBox="0 0 18 12" width="18" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 6.9L4.143 10.5L12 1.5M17 1.563L8.428 10.563L8 10"
      stroke="var(--primary-theme-color)"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

export default React.memo(MyMessage);
